import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  CreditCard, 
  Check, 
  Clock, 
  Star,
  ArrowLeft,
  Lightning,
  Trash,
  Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [activeTab, setActiveTab] = useState('piano');
  const [syncing, setSyncing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [redeemingPromo, setRedeemingPromo] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncSubscription = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/subscription/sync`);
      if (response.data.status === 'synced') {
        toast.success('Abbonamento sincronizzato!');
        await checkAuth();
        await fetchSubscriptionStatus();
      } else {
        toast.error('Nessun abbonamento trovato su Stripe');
      }
    } catch (err) {
      toast.error('Errore sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  const verifyPayment = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/subscription/verify/${sessionId}`);
      if (response.data.status === 'success') {
        toast.success('Pagamento completato con successo!');
        await checkAuth();
        await fetchSubscriptionStatus();
        navigate('/subscription', { replace: true });
      } else {
        // Payment went through on Stripe but verify didn't catch it - sync
        await syncSubscription();
        navigate('/subscription', { replace: true });
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
      // Try sync as fallback
      await syncSubscription();
      navigate('/subscription', { replace: true });
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const response = await axios.post(`${API}/subscription/reactivate`);
      toast.success(response.data.message);
      await fetchSubscriptionStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella riattivazione');
    }
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Inserisci un codice');
      return;
    }
    setRedeemingPromo(true);
    try {
      const response = await axios.post(`${API}/promo-codes/redeem`, { code: promoCode });
      toast.success(response.data.message);
      setPromoCode('');
      await checkAuth();
      await fetchSubscriptionStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codice non valido');
    } finally {
      setRedeemingPromo(false);
    }
  };

  const handleCheckout = (packageId) => {
    // Redirect to in-app embedded Stripe Elements checkout.
    setProcessingCheckout(true);
    navigate(`/subscribe?plan=${packageId}`);
  };

  const handleCancelSubscription = async () => {
    setCancelingSubscription(true);
    try {
      const response = await axios.post(`${API}/subscription/cancel`);
      toast.success(response.data.message);
      fetchSubscriptionStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella cancellazione');
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await axios.post(`${API}/subscription/portal`);
      window.location.href = response.data.portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore apertura portale');
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Inserisci la password per confermare');
      return;
    }
    setDeletingAccount(true);
    try {
      // Verify password first
      await axios.post(`${API}/auth/login`, { email: user.email, password: deletePassword });
      // Then delete
      await axios.delete(`${API}/auth/delete-account`);
      toast.success('Account eliminato con successo');
      logout();
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password errata o errore nella cancellazione');
    } finally {
      setDeletingAccount(false);
    }
  };

  const getStatusBadge = () => {
    if (!subscription) return null;
    if (subscription.plan_type === 'yearly') {
      return (
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2">
          <Star size={18} weight="fill" />
          <span className="font-bold">Piano Annuale — {subscription.days_remaining} giorni rimasti</span>
        </div>
      );
    }
    if (subscription.plan_type === 'monthly') {
      return (
        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2">
          <Check size={18} weight="bold" />
          <span className="font-bold">Piano Mensile Attivo</span>
        </div>
      );
    }
    if (subscription.plan_type === 'trial') {
      return (
        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2">
          <Clock size={18} weight="fill" />
          <span className="font-bold">{subscription.days_remaining} giorni di prova rimasti</span>
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-2 bg-red-100 text-red-800 px-4 py-2">
        <Clock size={18} />
        <span className="font-bold">Periodo di prova scaduto</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <p className="text-zinc-500">Caricamento...</p>
      </div>
    );
  }

  const isPaidUser = subscription?.plan_type === 'yearly' || subscription?.plan_type === 'monthly';

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="subscription-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-zinc-100 transition-colors" data-testid="back-button">
            <ArrowLeft size={24} weight="bold" className="text-zinc-700" />
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-zinc-900 tracking-tight">Account</h1>
            <p className="text-sm text-zinc-500">Gestisci piano e account</p>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex gap-0">
          {[
            { id: 'piano', label: 'Piano' },
            { id: 'account', label: 'Account' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-zinc-900 text-zinc-900'
                  : 'border-transparent text-zinc-400 hover:text-zinc-600'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* TAB: Piano */}
        {activeTab === 'piano' && (
          <>
            {/* Current Status */}
            <div className="bg-white border border-zinc-200 p-6 mb-8">
              <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">Stato Attuale</h2>
              {getStatusBadge()}
              
              {subscription?.canceling && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4" data-testid="cancel-confirmation-card">
                  <p className="text-sm font-black text-amber-900 tracking-tight mb-1">
                    ✓ Abbonamento disdetto correttamente
                  </p>
                  <p className="text-xs text-amber-800 leading-relaxed mb-3">
                    Non verranno effettuati altri addebiti. Continui ad avere accesso completo fino al{' '}
                    <strong>
                      {subscription.subscription_ends_at
                        ? new Date(subscription.subscription_ends_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
                        : '—'}
                    </strong>
                    {subscription.days_remaining != null && ` · ancora ${subscription.days_remaining} ${subscription.days_remaining === 1 ? 'giorno' : 'giorni'}`}.
                    <br />
                    Ti abbiamo inviato una <strong>email di conferma</strong> a {user?.email}.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={handleManageBilling}
                      variant="outline"
                      className="rounded-none border-amber-300 text-amber-900 hover:bg-amber-100 text-xs font-bold"
                      data-testid="view-on-stripe-btn"
                    >
                      Vedi su Stripe →
                    </Button>
                  </div>
                </div>
              )}
              
              {subscription?.plan_type === 'trial' && (
                <p className="text-sm text-zinc-500 mt-4">
                  Stai utilizzando la versione di prova gratuita. Scegli un piano per continuare.
                </p>
              )}
              
              {subscription?.plan_type === 'expired' && (
                <div>
                  <p className="text-sm text-red-600 mt-4">
                    Il tuo periodo di prova è scaduto. Scegli un piano per continuare.
                  </p>
                  <p className="text-xs text-zinc-400 mt-2">
                    Hai già pagato ma non vedi l'abbonamento attivo?
                  </p>
                  <Button
                    variant="outline"
                    onClick={syncSubscription}
                    disabled={syncing}
                    className="rounded-none border-zinc-300 text-sm mt-2"
                    data-testid="sync-subscription-btn"
                  >
                    {syncing ? 'Sincronizzazione...' : 'Sincronizza con Stripe'}
                  </Button>
                </div>
              )}

              {/* Manage subscription actions */}
              {isPaidUser && subscription?.has_stripe && (
                <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-zinc-100">
                  <Button
                    variant="outline"
                    onClick={handleManageBilling}
                    className="rounded-none border-zinc-300 text-sm"
                    data-testid="manage-billing-btn"
                  >
                    <CreditCard size={18} className="mr-2" />
                    Gestisci pagamento
                  </Button>
                  {!subscription?.canceling ? (
                    <Button
                      variant="outline"
                      onClick={handleCancelSubscription}
                      disabled={cancelingSubscription}
                      className="rounded-none border-red-300 text-red-600 hover:bg-red-50 text-sm"
                      data-testid="cancel-subscription-btn"
                    >
                      {cancelingSubscription ? 'Annullamento...' : 'Disdici abbonamento'}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleReactivateSubscription}
                      className="rounded-none border-green-300 text-green-700 hover:bg-green-50 text-sm"
                      data-testid="reactivate-subscription-btn"
                    >
                      Riattiva abbonamento
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Pricing Plans */}
            {!isPaidUser && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Monthly */}
                <div className="bg-white border border-zinc-200 p-6">
                  <h3 className="text-lg font-bold text-zinc-900 mb-4">Piano Mensile</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-black text-zinc-900">&euro;6,99</span>
                    <span className="text-zinc-500">/mese</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {['Contratti illimitati', '5 lingue disponibili', 'Firma remota ospiti', 'PDF scaricabili', 'Generatore ricevute affitto breve', 'Template personalizzabili'].map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-zinc-600">
                        <Check size={18} className="text-green-600 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleCheckout('monthly')}
                    disabled={processingCheckout}
                    className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-6"
                    data-testid="monthly-plan-button"
                  >
                    {processingCheckout ? 'Elaborazione...' : 'Scegli Mensile'}
                  </Button>
                </div>

                {/* Yearly */}
                <div className="bg-white border-2 border-zinc-900 p-6 relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zinc-900 text-white px-4 py-1 text-xs font-bold uppercase">
                    Risparmia 48%
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-zinc-900">Piano Annuale</h3>
                    <Lightning size={24} weight="fill" className="text-amber-500" />
                  </div>
                  <div className="mb-6">
                    <span className="text-4xl font-black text-zinc-900">&euro;49,99</span>
                    <span className="text-zinc-500">/anno</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {['Contratti illimitati', '5 lingue disponibili', 'Firma remota ospiti', 'PDF scaricabili', 'Generatore ricevute affitto breve', 'Template personalizzabili', 'Risparmio del 48%'].map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-zinc-600">
                        <Check size={18} className="text-green-600 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleCheckout('yearly')}
                    disabled={processingCheckout}
                    className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-6"
                    data-testid="yearly-plan-button"
                  >
                    {processingCheckout ? 'Elaborazione...' : 'Scegli Annuale'}
                  </Button>
                </div>
              </div>
            )}

            {/* Promo Code Section */}
            <div className="bg-white border border-zinc-200 p-6 sm:p-8 mt-6" data-testid="promo-code-section">
              <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-4">Hai un codice promozionale?</h3>
              <p className="text-xs text-zinc-500 mb-4">Inserisci il codice per attivare un piano gratuito o promozionale</p>
              <div className="flex gap-3">
                <Input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Inserisci codice"
                  className="rounded-none uppercase tracking-wider font-mono"
                  data-testid="promo-code-input"
                />
                <Button
                  onClick={handleRedeemPromo}
                  disabled={redeemingPromo || !promoCode.trim()}
                  className="rounded-none bg-teal-700 hover:bg-teal-800 text-white font-bold text-sm px-6 flex-shrink-0"
                  data-testid="redeem-promo-btn"
                >
                  {redeemingPromo ? 'Verifica...' : 'Attiva'}
                </Button>
              </div>
            </div>

            {/* Already paid */}
            {isPaidUser && !subscription?.canceling && (
              <div className="bg-green-50 border border-green-200 p-6 sm:p-8 text-center">
                <Star size={40} weight="fill" className="text-green-600 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-green-900 mb-2">Grazie per il tuo supporto!</h2>
                <p className="text-sm text-green-700">Il tuo abbonamento è attivo. Scade tra {subscription.days_remaining} giorni.</p>
              </div>
            )}
          </>
        )}

        {/* TAB: Account */}
        {activeTab === 'account' && (
          <>
            {/* Account Info */}
            <div className="bg-white border border-zinc-200 p-6 mb-6">
              <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">Informazioni Account</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Nome</span>
                  <span className="font-medium text-zinc-900">{user?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Email</span>
                  <span className="font-medium text-zinc-900">{user?.email}</span>
                </div>
              </div>
            </div>

            {/* Change Password */}
            <div className="bg-white border border-zinc-200 p-6 mb-6">
              <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">Password</h2>
              <p className="text-sm text-zinc-500 mb-4">Per cambiare la password usa la funzione "Password dimenticata" dalla schermata di login.</p>
              <Button
                variant="outline"
                onClick={() => { logout(); navigate('/forgot-password'); }}
                className="rounded-none border-zinc-300 text-sm"
                data-testid="change-password-btn"
              >
                Cambia password
              </Button>
            </div>

            {/* Delete Account */}
            <div className="bg-white border border-red-200 p-6">
              <h2 className="text-sm font-bold uppercase tracking-wider text-red-500 mb-2">Zona pericolosa</h2>
              <p className="text-sm text-zinc-500 mb-3">
                L'eliminazione dell'account è irreversibile. Tutti i tuoi dati, contratti, template, proprietà, pulizie, contabilità e log verranno cancellati permanentemente.
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-4">
                <p className="text-xs font-black uppercase tracking-wider text-amber-800 mb-1 inline-flex items-center gap-1.5">
                  <Warning size={12} weight="fill" /> Abbonamenti Stripe
                </p>
                <p className="text-[11px] text-amber-900 leading-snug">
                  Eventuali abbonamenti <strong>mensili o annuali</strong> attivi verranno
                  <strong> cancellati automaticamente</strong> in modo trasparente:
                  nessun addebito futuro sulla tua carta. Eventuali pagamenti già effettuati non sono rimborsabili.
                </p>
              </div>

              {!showDeleteConfirm ? (
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="rounded-none border-red-300 text-red-600 hover:bg-red-50 text-sm"
                  data-testid="delete-account-btn"
                >
                  <Trash size={18} className="mr-2" />
                  Elimina account
                </Button>
              ) : (
                <div className="bg-red-50 border border-red-200 p-4 mt-4" data-testid="delete-confirm-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Warning size={20} className="text-red-600" />
                    <p className="text-sm font-bold text-red-800">Conferma eliminazione account</p>
                  </div>
                  <p className="text-xs text-red-600 mb-4">Inserisci la tua password per confermare l'eliminazione definitiva di tutti i dati.</p>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-bold text-red-700">Password</Label>
                      <Input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder="La tua password"
                        className="rounded-none border-red-300 mt-1"
                        data-testid="delete-password-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount || !deletePassword}
                        className="rounded-none bg-red-600 hover:bg-red-700 text-white font-bold text-sm"
                        data-testid="confirm-delete-btn"
                      >
                        {deletingAccount ? 'Eliminazione...' : 'Elimina definitivamente'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); }}
                        className="rounded-none border-zinc-300 text-sm"
                        data-testid="cancel-delete-btn"
                      >
                        Annulla
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
